import _ from 'lodash';
import { createSelector } from 'reselect';
import moment from 'moment';
import { getFormValues } from 'redux-form';

import * as CommonStateSelectors from 'State/selectors';
import {
  ProductionRecordFieldEnum as ProdRecordFields,
  ProductionRecipeLeftoverEnum as ProdRecipeLeftoverFields,
  ProductionRecordDetailFieldEnum as ProdRecordDetailFields,
} from 'Common/FieldEnum';
import { createMap } from 'Common/Helpers/format';
import { isDetailIngredient } from 'Pages/manageproduction/helpers/productionRecord';
import { DATE_FORMAT_DAY_MONTH_NUMBER } from 'Common/Constants';
import { PRODUCTION_PLAN_FORM_NAME } from 'Common/FormEnum';

import { NUMBER_OF_DAYS_TO_DISPLAY, Modals } from '../../helpers/Constants';
import * as ProductionRecordHelpers from '../../helpers/productionRecord';

const prodPlanFormValuesSelector = getFormValues(PRODUCTION_PLAN_FORM_NAME);

const root = (state) =>
  _.get(state, 'ManageProductionState.editProduction', {});

export const getFetching = (state) => _.get(root(state), 'fetching');

export const getStartFromIndex = (state) =>
  _.get(root(state), 'startFromIndex', 0);

export const getProdRecords = (state) => _.get(root(state), 'prodRecords');

export const getProdRecordDates = createSelector(
  getProdRecords,
  (prodRecords) => {
    return prodRecords.map((p, i) => {
      return {
        value: p[ProdRecordFields.Date],
        index: i,
      };
    });
  }
);

export const getProdRecordDatesToShow = createSelector(
  getProdRecordDates,
  getStartFromIndex,
  (prodRecordDates, startFromIndex) => {
    if (NUMBER_OF_DAYS_TO_DISPLAY > prodRecordDates.length) {
      return prodRecordDates;
    }

    const endIndex = startFromIndex + NUMBER_OF_DAYS_TO_DISPLAY;

    if (endIndex > prodRecordDates.length) {
      let dates = prodRecordDates.slice(startFromIndex, prodRecordDates.length);
      const remainingDates = endIndex - prodRecordDates.length;

      // Pull dates from beginning
      dates = dates.concat(prodRecordDates.slice(0, remainingDates));

      return dates;
    }

    return prodRecordDates.slice(startFromIndex, endIndex);
  }
);

export const getViewProdRecordIndex = (state) =>
  _.get(root(state), 'viewProdRecordIndex', 0);

export const getProdRecord = createSelector(
  getProdRecords,
  getViewProdRecordIndex,
  (prodRecords, viewProdRecordIndex) => {
    const prodRecord = prodRecords[viewProdRecordIndex];

    return prodRecord;
  }
);

export const getProdRecordFeedingFigure = createSelector(
  getProdRecord,
  (prodRecord) => _.get(prodRecord, ProdRecordFields.FeedingFigure, 0)
);

export const getProdRecordCopy = (state) => {
  const prodRecord = getProdRecord(state);

  return _.cloneDeep(prodRecord);
};

export const getProdDetails = (state) => {
  const prodRecord = getProdRecord(state);

  if (prodRecord) {
    return prodRecord[ProdRecordFields.Details];
  }
  return [];
};

export const getError = (state) => _.get(root(state), 'error');

export const getTemperaturesInUse = createSelector(
  getProdRecords,
  (prodRecords) => {
    return ProductionRecordHelpers.getTemperaturesInUse(prodRecords);
  }
);

export const getServingLines = (i, state) => {
  const details = getProdDetails(state);
  
  if(details) {
    const detail = details[i];
    return detail[ProdRecordDetailFields.ServingLine] || [];
  }
  return [];
};

export const getFetchingCloseProd = (state) =>
  _.get(root(state), 'fetchingCloseProd', false);

export const getFetchingStartOver = (state) =>
  _.get(root(state), 'fetchingStartOver', false);

export const getSaveSuccessful = (state) =>
  _.get(root(state), 'saveSuccessful', false);

export const getTemperatures = (state) =>
  _.get(root(state), 'temperatures', ['']);

export const getInitialProdRecords = (state) =>
  _.get(root(state), 'initialProdRecords', []);

export const getCloseSuccessful = (state) =>
  _.get(root(state), 'closeSuccessful', false);

export const getStartOverSuccessful = (state) =>
  _.get(root(state), 'startOverSuccessful', false);

export const getLoadedRecordsMap = (state) =>
  _.get(root(state), 'loadedRecordsMap', {});

export const getExpandedRecipesByProdRecord = (state) =>
  _.get(root(state), 'expandedRecipesByProdRecord', {});

export const getExpandedRecipesByProdRecordCopy = (state) => ({
  ..._.get(root(state), 'expandedRecipesByProdRecord'),
});

export const getExpandedRecipes = (state) => {
  const viewProdRecordIndex = getViewProdRecordIndex(state);
  const expandedRecipesByProdRecord = getExpandedRecipesByProdRecord(state);

  return _.get(expandedRecipesByProdRecord, viewProdRecordIndex, {});
};

export const getFeedingFigure = (state) => {
  const prodRecord = getProdRecord(state);

  return Number(prodRecord[ProdRecordFields.FeedingFigure]);
};

export const getSiteId = (state) =>
  _.get(getProdRecord(state), ProdRecordFields.SiteId, null);

export const getServingDate = (state) =>
  _.get(getProdRecord(state), ProdRecordFields.ServingDT, null);

export const getRecipeLeftovers = (state) =>
  _.get(root(state), 'recipeLeftovers', []);

export const getCondensedLeftoversList = createSelector(
  getRecipeLeftovers,
  (recipeLeftovers) => {
    return ProductionRecordHelpers.condenseRecipeLeftovers(recipeLeftovers);
  }
);

export const getAvailableLeftovers = createSelector(
  getRecipeLeftovers,
  getServingDate,
  (recipeLeftovers, servingDate) => {
    const leftovers = recipeLeftovers.filter((rl) => {
      return (
        moment(rl[ProdRecipeLeftoverFields.UseByDT]).isSameOrAfter(
          servingDate
        ) && Number(rl[ProdRecipeLeftoverFields.Portions]) > 0
      );
    });

    return ProductionRecordHelpers.condenseRecipeLeftovers(leftovers);
  }
);

export const getProdRecordRecipesById = createSelector(
  getProdDetails,
  (details) => {
    const detailRecipes = details.filter(
      (detail) => !isDetailIngredient(detail)
    );
    return createMap(detailRecipes, ProdRecordDetailFields.RecipeId);
  }
);

export const getDetailsGroupedByRecipeCategory = createSelector(
  getProdDetails,
  getExpandedRecipes,
  (details, expandedRecipes) => {
    return ProductionRecordHelpers.groupDetailsByRecipeCategory(
      details,
      expandedRecipes
    );
  }
);

export const getRecipeExpanded = (state, recipeId, isLeftover) => {
  const expandedRecipes = getExpandedRecipes(state);
  const id = ProductionRecordHelpers.createToggleId(recipeId, isLeftover);

  return expandedRecipes[id];
};

export const getRecipeCategoryPlannedPercentageMessage = (
  state,
  recipeCategoryId
) => {
  const feedingFigure = getFeedingFigure(state);
  const percentage = getRecipeCategoryFieldTotal(
    state,
    ProdRecordDetailFields.PlannedPercentage,
    recipeCategoryId
  );

  let extraOrMissingAmount = 0;
  let result = '';

  if (
    ProductionRecordHelpers.isEntree(recipeCategoryId) ||
    ProductionRecordHelpers.isMilk(recipeCategoryId)
  ) {
    if (feedingFigure > 0) {
      const categoryTotal = getRecipeCategoryFieldTotal(
        state,
        ProdRecordDetailFields.ReimbursableMeals,
        recipeCategoryId
      );

      extraOrMissingAmount = feedingFigure - categoryTotal;

      if (extraOrMissingAmount) {
        result = `${percentage.toFixed(2)}% / ${
          extraOrMissingAmount > 0 ? '' : '+'
        }${-extraOrMissingAmount}`;
      } else {
        result = '100.00%';
      }
    }
  } else {
    result = `${percentage.toFixed(2)}%`;
  }

  return result;
};

export const getRecipeCategoryPlannedAlacarte = (state, recipeCategoryId) => {
  const detailsByRecipeCategory = getDetailsGroupedByRecipeCategory(state);

  if (!detailsByRecipeCategory[recipeCategoryId]) {
    return 0;
  }

  return Number(
    ProductionRecordHelpers.computeColumnTotal(
      detailsByRecipeCategory[recipeCategoryId],
      ProdRecordDetailFields.PlannedAlacarte
    )
  );
};

export const getRecipeCategoryPlannedAdult = (state, recipeCategoryId) => {
  const detailsByRecipeCategory = getDetailsGroupedByRecipeCategory(state);

  if (!detailsByRecipeCategory[recipeCategoryId]) {
    return 0;
  }

  return Number(
    ProductionRecordHelpers.computeColumnTotal(
      detailsByRecipeCategory[recipeCategoryId],
      ProdRecordDetailFields.PlannedAdult
    )
  );
};

export const getRecipeCategoryPlannedTotal = (state, recipeCategoryId) => {
  const detailsByRecipeCategory = getDetailsGroupedByRecipeCategory(state);
  const prodRecord = getProdRecord(state);

  if (!detailsByRecipeCategory[recipeCategoryId] || !prodRecord) {
    return 0;
  }

  return ProductionRecordHelpers.isEntree(recipeCategoryId)
    ? ProductionRecordHelpers.computeFeedingFigureTotal(prodRecord)
    : _.sumBy(detailsByRecipeCategory[recipeCategoryId], (d) => {
        if (ProductionRecordHelpers.isDetailIngredient(d)) {
          return 0;
        }
        return ProductionRecordHelpers.computePlannedTotal(d);
      });
};

export const getRecipeCategoryFieldTotal = (state, field, recipeCategoryId) => {
  const detailsByRecipeCategory = getDetailsGroupedByRecipeCategory(state);

  if (!detailsByRecipeCategory[recipeCategoryId]) {
    return 0;
  }

  return Number(
    ProductionRecordHelpers.computeColumnTotal(
      detailsByRecipeCategory[recipeCategoryId],
      field
    )
  );
};

export const getRecipeCategoryServedReimbursableTotal = (
  state,
  recipeCategoryId
) => {
  const detailsByRecipeCategory = getDetailsGroupedByRecipeCategory(state);

  if (!detailsByRecipeCategory[recipeCategoryId]) {
    return 0;
  }

  return _.sumBy(detailsByRecipeCategory[recipeCategoryId], (d) => {
    if (ProductionRecordHelpers.isDetailIngredient(d)) {
      return 0;
    }
    return ProductionRecordHelpers.computeReimbServedTotal(d);
  });
};

export const getModalTitle = (state) => {
  const prodRecord = getProdRecord(state);
  const sitesMap = CommonStateSelectors.getSitesMap(state);

  let title = '';

  if (prodRecord) {
    const site =
      _.get(sitesMap, [prodRecord[ProdRecordFields.SiteId], 'Description']) ||
      prodRecord[ProdRecordFields.SiteId];

    const date = moment(prodRecord[ProdRecordFields.Date]).format(
      DATE_FORMAT_DAY_MONTH_NUMBER
    );

    const standard = prodRecord[ProdRecordFields.Standard];

    title = `${site} - ${date} - ${standard}`;
  }
  return title;
};

export const getShowProdPlanModal = (state) =>
  _.get(root(state), Modals.ProductionPlan, false);

export const getShowCloseModal = (state) =>
  _.get(root(state), Modals.Close, false);

export const getShowPrintModal = (state) =>
  _.get(root(state), Modals.Print, false);

export const getProdPlanFormValues = (state) =>
  prodPlanFormValuesSelector(state);



// WEBPACK FOOTER //
// ./src/pages/manageproduction/state/editproduction/selectors.js